import { UIView, UIViewController } from "uicore-ts"


// Base class for any view controller registered in
// UIRootViewController.detailsViewControllers — i.e. anything shown through
// the framework's _detailsDialogView backdrop (offer details, log events,
// etc).
//
// Why this class exists:
//
// UIRootViewController.performDefaultLayout sizes and positions a details
// controller's view itself, not just its content:
//
//   detailsViewController.view.frame = backgroundView.frame
//       .rectangleWithInset(paddingLength)
//       .rectangleWithWidth(detailsWidth, 0.5)   // centers + caps the width
//       .rectangleWithHeight(...)
//
// `detailsWidth` comes from calling intrinsicContentWidth() on that same
// view. That view is just a plain UIView (constructed generically by
// lazyViewControllerObjectWithClass), so by default intrinsicContentWidth()
// has no opinion about a max width — without overriding it, the dialog would
// stretch to fill the entire page width.
//
// The pitfall this class prevents: once the root controller has already
// centered and width-capped this.view, a subclass must NOT repeat that
// centering/width-capping inside its own layoutViewSubviews() (e.g. by
// inset-ing this.view.bounds again and re-centering the content view within
// it). Doing so centers an already-centered box a second time and shrinks an
// already-capped width a second time — which is what caused a dialog to
// render shifted into a corner instead of properly filling its slot. This
// base class is the one place that talks to the root controller's sizing
// contract, so subclasses never need to think about it.
//
// How to use it: subclass this, declare maxWidth / widthViewportRatio (your
// own design tokens), add your dialog's content view as a subview of
// this.view, and assign it to contentView. That's it — no constructor
// boilerplate, no layoutViewSubviews override needed in the subclass. The
// content view fills this.view's bounds, with height coming from the content
// view's own intrinsic height (not from this.view.bounds.height).

export abstract class RHDetailsDialogViewController extends UIViewController {
    
    /** Maximum dialog width in pixels. */
    protected abstract readonly maxWidth: number
    
    /** Dialog width as a fraction of the page width (0–1); the smaller of
     *  this and maxWidth is used. */
    protected abstract readonly widthViewportRatio: number
    
    /** The dialog's content view. Must already be added as a subview of
     *  this.view by the subclass. */
    protected abstract readonly contentView: UIView
    
    constructor(view: UIView) {
        super(view)
        
        // Read by UIRootViewController.performDefaultLayout to compute
        // detailsWidth, which it then uses to center and size this.view.
        this.view.intrinsicContentWidth = () => Math.min(
            this.maxWidth,
            UIView.pageWidth * this.widthViewportRatio
        )
    }
    
    
    override layoutViewSubviews(): void {
        super.layoutViewSubviews()
        
        // this.view is already centered and width-capped by the root
        // controller via intrinsicContentWidth above — just fill it.
        this.view.bounds
            .rectangleWithHeight(this.contentView)
            .assignedAsFrameOfView(this.contentView)
    }
    
}
